/**
 * shared/narration/speakable.ts — §4.7. The only prosody lever that exists.
 *
 * CHATTERBOX HAS NO SSML, NO PHONEMES AND NO PROSODY MARKUP. Verified on the live API, twice
 * (2026-08-06 and again 2026-08-07: `/generate` accepts `{text, voice, exaggeration?, cfg_weight?,
 * temperature?, variant?}` and nothing else). So there are exactly two ways to change how a sentence
 * is read: the silence we insert around it (`shared/narration/pacing.ts`) and the TEXT WE SEND.
 * This file is the second one. Do not design a feature that assumes a markup channel exists; there
 * is not one to add.
 *
 * ── What this function's output IS ──────────────────────────────────────────
 * `ttsText` — the exact string posted to `/generate`, and the string hashed into `clipKey`
 * (`shared/narration/keys.ts`) and into `sentences.ttsTextSha`. That is a load-bearing property and
 * it is the reason LAW 7 forbids a version constant here: change a rule in this file and only the
 * sentences whose `ttsText` actually changed become `missing`. A `SPEAKABLE_VERSION` mixed into the
 * key would turn a Roman-numeral bugfix affecting forty sentences into hundreds of GPU-hours of
 * re-narration across the library, discovered as a bill rather than as an error. There is no version
 * constant in this file and there must never be one.
 *
 * ── What it deliberately does NOT touch ─────────────────────────────────────
 * Round-tripping spark-1 through the Nemotron ASR (§4.7) measured what the model already gets right:
 * `1861`, `1,204`, `50%`, `No. 7`, `$1,200`, `3:45 p.m.`, `Mr.`, and a bare em dash all came back
 * correct. **Rewriting those would be a change with no measurement behind it**, and every rewrite is
 * a chance to make a correct reading wrong. The stages below fire only on the four categories that
 * measurably failed — scripture references, Roman numerals, dates, footnote markers and initials —
 * plus typography, which is cosmetic and safe.
 *
 * ── This is NOT a segmenter (LAW 4) ─────────────────────────────────────────
 * `splitForSynthesis()` cuts an ALREADY-SEGMENTED sentence into sub-utterances so a very long
 * sentence never reaches Chatterbox's own `_chunk_text` (server-side, `MAX_CHARS = 450`). It does not
 * decide what a sentence is, it does not produce sids, and its boundaries are emitted into the
 * already-reserved `Cue.w[]` rather than becoming cues. There is one segmenter, it is
 * `shared/text/segment.ts`, it runs at ingest, and its output is stored.
 *
 * ── Measured 2026-08-07, and it strengthens the per-sentence decision ───────
 * §4.7 blamed the uncontrollable inter-sentence pause on `_chunk_text` firing at 450 chars. That
 * attribution is too narrow: the model inserts 268–1872 ms pauses between sentences at EVERY request
 * length, including a 120-char request far below the chunk boundary. Tuning the request size chases a
 * boundary that does not exist. One sentence per request is what converts an unmeasurable pause into
 * `PACING.prose.sentence = 260 ms` that we chose.
 *
 * `shared/` is bundled by the client: no I/O, no `node:` imports, and every function here is pure.
 */

import type { NarrationStyle } from "./pacing.ts";
// The invisible-character set is NOT re-typed here. `shared/text/normalize.ts` builds it from code
// points on purpose — a literal zero-width character inside a `/[...]/` is invisible in the editor,
// in a diff and in a review, so nobody can see it and nobody can see it removed. One list, one place.
import { collapseWhitespace, stripInvisible } from "../text/normalize.ts";
// Devanagari is not readable by Chatterbox on either variant (measured 2026-08-08 — see the file's
// header). The transliterator returns its input BY IDENTITY when there is no Devanagari in it, which
// is why this import costs an English book one regex test per sentence and nothing else.
import { transliterateDevanagari } from "../text/devanagari.ts";

// ─── the public shape ────────────────────────────────────────────────────────

export interface SpeakableOptions {
  /**
   * The pronunciation lexicon and any per-sentence respelling, merged by the caller: printed form →
   * how to say it. P3 fills this from the `pronunciations` table and `sentence_overrides.ttsText`.
   */
  overrides?: Readonly<Record<string, string>>;
  /**
   * `"scripture"` additionally strips INLINE VERSE NUMBERS, because hearing "four" before every
   * clause makes the Bible unlistenable. It is opt-in per book (`books.narrationStyle`) and never
   * inferred, because the same rule applied to prose would eat "5 Loaves".
   */
  style?: NarrationStyle;
  /** Sub-utterance ceiling. Well under Chatterbox's own 450-char `_chunk_text` boundary. */
  maxChars?: number;
  /** Never leave a fragment shorter than this if a longer break is available. */
  minChars?: number;
}

export interface Speakable {
  /** The whole normalised utterance. Hashed into `clipKey` and `sentences.ttsTextSha`. */
  ttsText: string;
  /**
   * What is actually posted to `/generate`, in order. One element for almost every sentence in
   * almost every book; more only when a single sentence is longer than `maxChars`. The stitcher
   * joins them with `PACING.seam` and emits their boundaries into `Cue.w[]`.
   */
  parts: string[];
  /**
   * `false` ⇒ printed but not spoken: an override mapped the whole utterance to `""`, or nothing
   * sayable survived (a row of asterisks, a bare URL). The caller stores no clip and emits no cue,
   * and the sentence still renders and is still addressable — it simply has no audio.
   */
  spoken: boolean;
}

/** Well under the 450-char boundary where Chatterbox chunks for us and inserts a pause we cannot measure. */
export const SYNTHESIS_MAX_CHARS = 250;
/** Below this a fragment is a gasp rather than a clause; used to reject a break, never to merge sentences. */
export const SYNTHESIS_MIN_CHARS = 40;

/**
 * The thirteen stages, in order. Each is exported below and each is pure, so a golden test can name
 * the stage that broke rather than reporting that "the normaliser changed".
 *
 * The ORDER is a specification, not an implementation detail:
 *   - overrides run FIRST, on verbatim text, because the keys a reader writes are the strings they
 *     see on the page. Run them after quote-stripping and half of them silently never match.
 *   - footnote markers run before NFKC, because NFKC folds `³` into `3` and the marker becomes
 *     indistinguishable from a number in the prose.
 *   - the ALL-CAPS pass runs after abbreviations, so `FBI` is still `FBI` and `THE END` is not
 *     shouted through a table lookup that no longer applies.
 *   - **transliteration runs LAST but one, after every English-specific stage and before
 *     `ensureTerminal`.** Both halves of that are deliberate. Running it late means the scripture,
 *     Roman-numeral, date, abbreviation, initials and ALL-CAPS passes only ever see Devanagari — and
 *     every one of them is anchored on `[A-Z]`, `\p{Lu}` or an ASCII word, so on Devanagari they are
 *     all no-ops. Run it early instead and those stages get a fresh Latin string to find false
 *     positives in: `Ram 3` would become a Bible citation in a Hindi book. Running it before
 *     `ensureTerminal` is what lets a danda count as the full stop it is.
 */
export function speakable(text: string, options: SpeakableOptions = {}): Speakable {
  const scripture = options.style === "scripture";

  let out = applyOverrides(text, options.overrides);
  // An override to "" is the documented way to say "printed, not spoken". Detected here, on the
  // trimmed result, before the later stages can put punctuation back and make it look sayable.
  const droppedByOverride = text.trim() !== "" && out.trim() === "";

  out = stripFootnoteMarkers(out);
  out = expandScripture(out, scripture);
  out = expandRomanNumerals(out);
  out = expandDatesAndOrdinals(out);
  out = expandAbbreviations(out);
  out = spaceInitials(out);
  out = normaliseTypography(out);
  out = softenAllCaps(out);
  out = dropUrlsAndEmails(out);
  out = transliterateDevanagari(out);
  out = ensureTerminal(out);

  const spoken = !droppedByOverride && HAS_SPEECH.test(out);
  if (!spoken) return { ttsText: "", parts: [], spoken: false };

  return {
    ttsText: out,
    parts: splitForSynthesis(out, { max: options.maxChars, min: options.minChars }),
    spoken: true,
  };
}

/** A string with no letter and no digit in it has nothing to say — `* * *`, `—`, `[]`. */
const HAS_SPEECH = /[\p{L}\p{N}]/u;

// ─── 1. overrides ────────────────────────────────────────────────────────────

/**
 * One alternation over every override key, LONGEST FIRST.
 *
 * Lifted from books.karti.ai `server/src/audio/narrate.ts`, including both of its hard-won details:
 *
 * **Longest first matters.** With keys for both `41` and `41.22`, the shorter one eats the prefix
 * and leaves `forty-one.22`.
 *
 * **The boundary guards are conditional, not a blanket `\b`,** because the keys are phrases and not
 * words. `Nila` needs a guard on both sides so it matches inside `Nila's` (an apostrophe is not a
 * letter) but not inside a longer name; `— K. T.` ends in punctuation, where `\b` would simply never
 * match and the override would look broken with nothing to point at.
 *
 * ⚠️ **A regex that fails to compile is swallowed.** A reader's pronunciation map must not be able to
 * break their own book: a wrong pronunciation is one right-click to fix, a failed render is a book
 * that does not narrate. Returning `null` narrates the prose as written.
 */
export function overridePattern(overrides?: Readonly<Record<string, string>>): RegExp | null {
  const keys = Object.keys(overrides ?? {}).filter((k) => k.length > 0);
  if (keys.length === 0) return null;

  const alternatives = keys
    .sort((a, b) => b.length - a.length)
    .map((key) => {
      const escaped = key.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
      const left = /^[\p{L}\p{N}]/u.test(key) ? "(?<![\\p{L}\\p{N}])" : "";
      const right = /[\p{L}\p{N}]$/u.test(key) ? "(?![\\p{L}\\p{N}])" : "";
      return `${left}${escaped}${right}`;
    });

  try {
    return new RegExp(alternatives.join("|"), "gu");
  } catch {
    return null;
  }
}

export function applyOverrides(text: string, overrides?: Readonly<Record<string, string>>): string {
  const pattern = overridePattern(overrides);
  if (pattern === null) return text;
  const map = overrides ?? {};
  return text.replace(pattern, (match) => map[match] ?? match);
}

// ─── 2. footnote markers ─────────────────────────────────────────────────────

/**
 * Defensive. Footnote markers are removed structurally at ingest (`worker/src/ingest/blocks.ts`
 * turns them into `notes` rows), so this stage should find nothing in an EPUB — but a plain-text
 * upload and a PDF text layer both carry them inline, and the measured failure is ugly:
 *
 *     "He agreed.3 The next"  →  "He agreed to point three the next"
 *
 * The digit merges with the full stop into a decimal. The lookbehind requires a LETTER before the
 * stop precisely so `3.14` and `p. 3` are untouched — in a decimal the character before the stop is
 * itself a digit.
 */
const FOOTNOTE_AFTER_STOP = /(?<=\p{L}[.!?])[\d]{1,3}(?=\s|$)/gu;
/** Superscript digits, U+00B9/B2/B3 and U+2070–2079. Struck before NFKC folds them into plain digits. */
const SUPERSCRIPT_DIGITS = /[¹²³⁰⁴-⁹]+/gu;
/** `[3]`, `[12]` — a bracketed number is never prose. `(3)` is left alone; it is often an enumeration. */
const BRACKETED_MARKER = /\[\s*\d{1,3}\s*\]/gu;

export const stripFootnoteMarkers = (text: string): string =>
  text.replace(BRACKETED_MARKER, "").replace(SUPERSCRIPT_DIGITS, "").replace(FOOTNOTE_AFTER_STOP, "");

// ─── 3. scripture ────────────────────────────────────────────────────────────

/**
 * The 66-book table. Karti named the Bible, so this is a day-one category rather than a nicety, and
 * it is the one the model fails worst:
 *
 *     "1 Cor 13:4-7"  →  "Coin Corinthians thirteen forty seven"
 *     "Gen 1:1"       →  "gen one"          (the verse number DROPPED ENTIRELY)
 *     "cf. Ps 23"     →  "CF twenty three"  ("Ps" dropped)
 *
 * `forms` are matched lowercased with dots removed, so `Cor.`, `cor` and `COR` are one entry.
 * `num` is the leading 1/2/3 (also accepted as I/II/III), spoken as First/Second/Third.
 */
interface ScriptureBook {
  spoken: string;
  num?: 1 | 2 | 3;
  forms: readonly string[];
}

const SCRIPTURE_BOOKS: readonly ScriptureBook[] = [
  { spoken: "Genesis", forms: ["genesis", "gen", "ge", "gn"] },
  { spoken: "Exodus", forms: ["exodus", "exod", "exo", "ex"] },
  { spoken: "Leviticus", forms: ["leviticus", "lev", "le", "lv"] },
  { spoken: "Numbers", forms: ["numbers", "num", "nu", "nm", "nb"] },
  { spoken: "Deuteronomy", forms: ["deuteronomy", "deut", "deu", "dt", "de"] },
  { spoken: "Joshua", forms: ["joshua", "josh", "jos", "jsh"] },
  { spoken: "Judges", forms: ["judges", "judg", "jdg", "jg"] },
  { spoken: "Ruth", forms: ["ruth", "rth", "ru"] },
  { spoken: "Samuel", num: 1, forms: ["samuel", "sam", "sa", "sm"] },
  { spoken: "Samuel", num: 2, forms: ["samuel", "sam", "sa", "sm"] },
  { spoken: "Kings", num: 1, forms: ["kings", "kgs", "kin", "ki"] },
  { spoken: "Kings", num: 2, forms: ["kings", "kgs", "kin", "ki"] },
  { spoken: "Chronicles", num: 1, forms: ["chronicles", "chron", "chr", "ch"] },
  { spoken: "Chronicles", num: 2, forms: ["chronicles", "chron", "chr", "ch"] },
  { spoken: "Ezra", forms: ["ezra", "ezr", "ez"] },
  { spoken: "Nehemiah", forms: ["nehemiah", "neh", "ne"] },
  { spoken: "Esther", forms: ["esther", "esth", "est", "es"] },
  { spoken: "Job", forms: ["job", "jb"] },
  { spoken: "Psalm", forms: ["psalms", "psalm", "pss", "ps", "psa", "psm"] },
  { spoken: "Proverbs", forms: ["proverbs", "prov", "prv", "pro", "pr"] },
  { spoken: "Ecclesiastes", forms: ["ecclesiastes", "eccles", "eccl", "ecc", "ec", "qoh"] },
  { spoken: "the Song of Solomon", forms: ["songofsolomon", "songofsongs", "song", "sos", "cant"] },
  { spoken: "Isaiah", forms: ["isaiah", "isa", "is"] },
  { spoken: "Jeremiah", forms: ["jeremiah", "jer", "je", "jr"] },
  { spoken: "Lamentations", forms: ["lamentations", "lam", "la"] },
  { spoken: "Ezekiel", forms: ["ezekiel", "ezek", "eze", "ezk"] },
  { spoken: "Daniel", forms: ["daniel", "dan", "da", "dn"] },
  { spoken: "Hosea", forms: ["hosea", "hos", "ho"] },
  { spoken: "Joel", forms: ["joel", "jl"] },
  { spoken: "Amos", forms: ["amos", "am"] },
  { spoken: "Obadiah", forms: ["obadiah", "obad", "ob"] },
  { spoken: "Jonah", forms: ["jonah", "jon", "jnh"] },
  { spoken: "Micah", forms: ["micah", "mic", "mc"] },
  { spoken: "Nahum", forms: ["nahum", "nah", "na"] },
  { spoken: "Habakkuk", forms: ["habakkuk", "hab", "hb"] },
  { spoken: "Zephaniah", forms: ["zephaniah", "zeph", "zep", "zp"] },
  { spoken: "Haggai", forms: ["haggai", "hag", "hg"] },
  { spoken: "Zechariah", forms: ["zechariah", "zech", "zec", "zc"] },
  { spoken: "Malachi", forms: ["malachi", "mal", "ml"] },
  { spoken: "Matthew", forms: ["matthew", "matt", "mat", "mt"] },
  { spoken: "Mark", forms: ["mark", "mrk", "mk", "mr"] },
  { spoken: "Luke", forms: ["luke", "luk", "lk"] },
  { spoken: "John", forms: ["john", "joh", "jhn", "jn"] },
  // NOT `act`: "Act 3" is a play, and `ROMAN_CONTEXT` below expands `Act III` for exactly that case.
  { spoken: "the Acts of the Apostles", forms: ["acts", "ac"] },
  { spoken: "Romans", forms: ["romans", "rom", "ro", "rm"] },
  { spoken: "Corinthians", num: 1, forms: ["corinthians", "cor", "co"] },
  { spoken: "Corinthians", num: 2, forms: ["corinthians", "cor", "co"] },
  { spoken: "Galatians", forms: ["galatians", "gal", "ga"] },
  { spoken: "Ephesians", forms: ["ephesians", "eph", "ep"] },
  { spoken: "Philippians", forms: ["philippians", "phil", "php", "pp"] },
  { spoken: "Colossians", forms: ["colossians", "col", "cl"] },
  { spoken: "Thessalonians", num: 1, forms: ["thessalonians", "thess", "thes", "th"] },
  { spoken: "Thessalonians", num: 2, forms: ["thessalonians", "thess", "thes", "th"] },
  { spoken: "Timothy", num: 1, forms: ["timothy", "tim", "ti"] },
  { spoken: "Timothy", num: 2, forms: ["timothy", "tim", "ti"] },
  { spoken: "Titus", forms: ["titus", "tit", "ti"] },
  { spoken: "Philemon", forms: ["philemon", "philem", "phlm", "pm"] },
  { spoken: "Hebrews", forms: ["hebrews", "heb", "hb"] },
  { spoken: "James", forms: ["james", "jas", "jm"] },
  { spoken: "Peter", num: 1, forms: ["peter", "pet", "pe", "pt"] },
  { spoken: "Peter", num: 2, forms: ["peter", "pet", "pe", "pt"] },
  { spoken: "John", num: 1, forms: ["john", "jhn", "jn", "jo"] },
  { spoken: "John", num: 2, forms: ["john", "jhn", "jn", "jo"] },
  { spoken: "John", num: 3, forms: ["john", "jhn", "jn", "jo"] },
  { spoken: "Jude", forms: ["jude", "jud", "jd"] },
  { spoken: "Revelation", forms: ["revelation", "revelations", "rev", "re", "rv"] },
];

const NUMBER_WORD = ["", "First", "Second", "Third"] as const;

/**
 * `"1cor"` → `"First Corinthians"`. Built once. A later entry never overwrites an earlier one, so
 * the ambiguous short forms resolve to the book that comes first in canonical order — `"ti"` is
 * Timothy rather than Titus, `"jn"` unnumbered is the Gospel rather than an epistle. Both of those
 * ARE guesses, and they are the reason a wrong reading is one lexicon entry away rather than a code
 * change: `overrides` runs first and beats this table outright.
 */
const SCRIPTURE_INDEX: ReadonlyMap<string, string> = (() => {
  const index = new Map<string, string>();
  for (const book of SCRIPTURE_BOOKS) {
    const spoken = book.num === undefined ? book.spoken : `${NUMBER_WORD[book.num]} ${book.spoken}`;
    for (const form of book.forms) {
      const key = `${book.num ?? ""}${form}`;
      if (!index.has(key)) index.set(key, spoken);
    }
  }
  return index;
})();

/**
 * ⚠️ **The forms that are also ordinary English, and the reason this guard exists at all.**
 *
 * A CHAPTER-ONLY reference has no `:` to prove it is a citation, so the only evidence left is the
 * book name — and a third of the canon is spelled like a normal word. Without this set the
 * normaliser rewrites *"Mark 3 steps ahead"* into a Bible citation and *"Is 40 the new 30?"* into
 * Isaiah, **in Karti's voice, with nothing anywhere reporting it.** That is the exact shape of
 * failure this repo keeps calling out: renders perfectly, sounds confident, is wrong.
 *
 * A reference carrying a verse (`Job 3:1`) is unambiguous and is always expanded. In a book whose
 * `narrationStyle` is `scripture`, so is a chapter-only one — there the citation reading is the
 * likely one, and the setting is a human decision rather than an inference.
 */
const PROSE_AMBIGUOUS_FORMS: ReadonlySet<string> = new Set([
  "acts", "ac", "am", "amos", "daniel", "esther", "hebrews", "hosea", "is", "james", "job", "joel",
  "john", "jonah", "jude", "judges", "kings", "la", "luke", "ma", "mark", "mr", "na", "numbers",
  "philemon", "pr", "revelation", "revelations", "romans", "ru", "ruth", "so", "song", "ti", "titus",
]);

/**
 * `[1 |I ]Book[.] chapter[:verse[-verse]]`.
 *
 * The book name is captured loosely and then LOOKED UP; an unknown name leaves the text untouched.
 * That is much safer than one giant alternation, and it is why `Smith 13:4` is not mangled.
 *
 * ⚠️ **The book token must start with a capital.** A citation always does, and this one character of
 * evidence is what keeps `he is 40 years old` from becoming Isaiah — the failure that made the guard
 * set above necessary in the first place.
 */
const SCRIPTURE_REF =
  /(?<![\p{L}\p{N}])(?:(?:([123])|(I{1,3}))\s*)?([A-Z][A-Za-z]{0,13}\.?(?:\s+of\s+[A-Za-z]+)?)\s+(\d{1,3})(?:\s*[:.]\s*(\d{1,3})(?:\s*[-–—]\s*(\d{1,3}))?)?(?![\p{L}\p{N}])/gu;

/** Verse numbers printed inline in a scripture layout: `4 Love is patient, 5 love is kind`. */
const INLINE_VERSE_NUMBER = /(?:^|(?<=[\s.,;:!?]))\d{1,3}\s+(?=\p{Lu})/gu;

/**
 * ⚠️ **A failed lookup must not CONSUME the text it looked at, and `String.replace` cannot express
 * that** — which is why this is an `exec` loop and not a one-line `.replace()`.
 *
 * The book name is captured loosely (`[A-Z][A-Za-z]{0,13}`) and then looked up, so any capitalised
 * word followed by a number is a candidate. In `See 1 Cor 13:4-7` the regex matches **`See 1`**
 * first, finds no book called "see", returns the text unchanged — and `lastIndex` has moved past the
 * `1`, so `1 Cor 13:4-7` can never match. `1 Cor 13:4-7` on its own expanded correctly and the same
 * reference inside a sentence did not, which is the shape a golden test written from the design's
 * table passes straight over: §4.7's row is the bare citation.
 *
 * That case is not exotic. "See", "Read", "In", "Cf" and every chapter heading that ends in a word
 * before a numbered book (`2 Kings`, `1 John`, `3 John`) are exactly how a citation appears in
 * running prose, in the one book Karti named. On a miss the scan restarts one character later so an
 * overlapping candidate is still reachable.
 */
export function expandScripture(text: string, scriptureStyle = false): string {
  const re = new RegExp(SCRIPTURE_REF.source, SCRIPTURE_REF.flags);
  let out = "";
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const [whole, digit, roman, name, chapter, verse, verseEnd] = match as unknown as [
      string,
      string | undefined,
      string | undefined,
      string,
      string,
      string | undefined,
      string | undefined,
    ];
    const spoken = spokenReference(digit, roman, name, chapter, verse, verseEnd, scriptureStyle);
    if (spoken === null) {
      // Not a citation. Rewind to just after where this candidate STARTED, so the text it wrongly
      // swallowed is offered to the scanner again.
      re.lastIndex = match.index + 1;
      continue;
    }
    out += text.slice(cursor, match.index) + spoken;
    cursor = match.index + whole.length;
    re.lastIndex = cursor;
  }
  out += text.slice(cursor);

  if (scriptureStyle) out = out.replace(INLINE_VERSE_NUMBER, "");
  return out;
}

/** How this reference is read aloud, or `null` when it is not a reference at all. */
function spokenReference(
  digit: string | undefined,
  roman: string | undefined,
  name: string,
  chapter: string,
  verse: string | undefined,
  verseEnd: string | undefined,
  scriptureStyle: boolean,
): string | null {
  const num = digit ?? (roman === undefined ? "" : String(roman.length));
  const form = name.toLowerCase().replace(/[.\s]/gu, "");
  const spoken = SCRIPTURE_INDEX.get(`${num}${form}`);
  if (spoken === undefined) return null;

  // See PROSE_AMBIGUOUS_FORMS. A numbered book (`1 John 4`) is already unambiguous — no prose
  // sentence begins "1 John 4" — so the guard only applies to unnumbered chapter-only refs.
  if (verse === undefined && !scriptureStyle && num === "" && PROSE_AMBIGUOUS_FORMS.has(form)) return null;

  const chapterNum = Number(chapter);
  // "Psalm twenty-three", never "Psalms, chapter twenty-three" — nobody reads it that way, and this
  // is the book the product was asked for by name.
  const head =
    spoken === "Psalm" ? `Psalm ${cardinalWords(chapterNum)}` : `${spoken}, chapter ${cardinalWords(chapterNum)}`;

  if (verse === undefined) return head;
  if (verseEnd === undefined) return `${head}, verse ${cardinalWords(Number(verse))}`;
  return `${head}, verses ${cardinalWords(Number(verse))} to ${cardinalWords(Number(verseEnd))}`;
}

// ─── 4. Roman numerals ───────────────────────────────────────────────────────

/**
 * ONLY in numeric context. Never in prose.
 *
 * `chapter XIV` came back as "chapter fifteen" — off by one, which is the worst possible kind of
 * wrong because it sounds completely confident. But `I`, `MIX` and `DIM` are also well-formed Roman
 * numerals AND English words, so a blanket rule would read "I went" as "one went". The context is
 * therefore an allow-list: standing alone as a whole heading, or immediately after one of these
 * words.
 *
 * Case-insensitive on the KEYWORD only — a heading is `Chapter XIV` and a cross-reference mid-prose
 * is `see chapter XIV`, and the measured failure was written the second way. The keyword is put back
 * exactly as the author typed it; only the numeral is rewritten.
 */
const ROMAN_CONTEXT = /\b(Chapter|Part|Book|Act|Scene|Volume|Canto|Section|Appendix|Psalm)\s+([IVXLCDM]+)\b/giu;
const ROMAN_ALONE = /^\s*([IVXLCDM]+)\s*\.?\s*$/u;
/** Canonical Roman form only: rejects `IIII`, `VX`, `DID`. `MIX` passes — hence the context rule above. */
const ROMAN_CANONICAL = /^(?=[MDCLXVI])M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/u;
const ROMAN_VALUES: Readonly<Record<string, number>> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };

/** The integer a canonical Roman numeral denotes, or `null` if it is not one. */
export function romanValue(s: string): number | null {
  if (!ROMAN_CANONICAL.test(s)) return null;
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    const here = ROMAN_VALUES[s[i]!]!;
    const next = i + 1 < s.length ? ROMAN_VALUES[s[i + 1]!]! : 0;
    total += here < next ? -here : here;
  }
  return total;
}

export function expandRomanNumerals(text: string): string {
  const alone = ROMAN_ALONE.exec(text);
  if (alone !== null) {
    const value = romanValue(alone[1]!);
    // A heading that is nothing but a numeral is a chapter number; spelling it as a word rather than
    // as digits removes the one remaining ambiguity, which is that "14" alone can be read as a year.
    if (value !== null) return cardinalWords(value);
  }
  return text.replace(ROMAN_CONTEXT, (whole, keyword: string, numeral: string) => {
    const value = romanValue(numeral);
    return value === null ? whole : `${keyword} ${cardinalWords(value)}`;
  });
}

// ─── 5. dates and ordinals ───────────────────────────────────────────────────

const MONTHS: readonly (readonly [string, RegExp])[] = [
  ["January", /^jan(uary)?$/iu],
  ["February", /^feb(ruary)?$/iu],
  ["March", /^mar(ch)?$/iu],
  ["April", /^apr(il)?$/iu],
  ["May", /^may$/iu],
  ["June", /^jun(e)?$/iu],
  ["July", /^jul(y)?$/iu],
  ["August", /^aug(ust)?$/iu],
  ["September", /^sep(t|tember)?$/iu],
  ["October", /^oct(ober)?$/iu],
  ["November", /^nov(ember)?$/iu],
  ["December", /^dec(ember)?$/iu],
];

const monthName = (token: string): string | null => {
  const bare = token.replace(/\./gu, "");
  for (const [name, test] of MONTHS) if (test.test(bare)) return name;
  return null;
};

const MONTH_DAY_YEAR = /\b([A-Z][a-z]{2,8})\.?\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s*(\d{4})\b/gu;
const DAY_MONTH_YEAR = /\b(\d{1,2})(?:st|nd|rd|th)?\s+([A-Z][a-z]{2,8})\.?\s+(\d{4})\b/gu;
const MONTH_DAY = /\b([A-Z][a-z]{2,8})\.?\s+(\d{1,2})(?:st|nd|rd|th)\b/gu;
const BARE_ORDINAL = /\b(\d{1,3})(st|nd|rd|th)\b/gu;

/**
 * `Jan. 3, 1945` came back as "Jan thirty nineteen forty five" — the day and the year merged into
 * one number and the month was left as a fragment. Two fixes, both minimal:
 *
 *  - the month is spelled out, so there is nothing to abbreviate away;
 *  - **the day becomes an ordinal WORD**, which is how the date is read aloud anyway and which makes
 *    it structurally impossible for it to merge with the digits after it.
 *
 * The YEAR IS LEFT AS DIGITS on purpose. `1861` was measured correct, and rewriting a reading the
 * model already gets right is a change with no measurement behind it.
 */
export function expandDatesAndOrdinals(text: string): string {
  let out = text.replace(MONTH_DAY_YEAR, (whole, month: string, day: string, year: string) => {
    const name = monthName(month);
    return name === null ? whole : `${name} ${ordinalWords(Number(day))}, ${year}`;
  });

  out = out.replace(DAY_MONTH_YEAR, (whole, day: string, month: string, year: string) => {
    const name = monthName(month);
    return name === null ? whole : `the ${ordinalWords(Number(day))} of ${name}, ${year}`;
  });

  out = out.replace(MONTH_DAY, (whole, month: string, day: string) => {
    const name = monthName(month);
    return name === null ? whole : `${name} ${ordinalWords(Number(day))}`;
  });

  // A printed ordinal is the same shape a date produces, and it is read the same way. `21st` is
  // reliably "twenty-first" spelled out; as digits-plus-suffix it is a coin toss between that and
  // "twenty-one st".
  return out.replace(BARE_ORDINAL, (whole, digits: string) => {
    const n = Number(digits);
    return n > 0 && n < 1000 ? ordinalWords(n) : whole;
  });
}

// ─── 6. abbreviations and units ──────────────────────────────────────────────

/**
 * Longest first, so `e.g.` is not eaten by a rule for `g.`.
 *
 * ⚠️ **Titles are NOT in this table.** `Mr.`, `Mrs.`, `Dr.` and `3:45 p.m.` were all measured
 * correct. Expanding them would be a change with no evidence behind it, and "Mister" read for "Mr."
 * in a Victorian novel is worse than the abbreviation. `No. 7` and `50%` are absent for the same
 * reason.
 *
 * What IS here is the set that measurably failed or that has no chance of being read: `cf.` came
 * back as the letters "CF".
 */
const ABBREVIATIONS: readonly (readonly [RegExp, string])[] = [
  [/\bi\.\s?e\.(?=\s|$)/gu, "that is"],
  [/\be\.\s?g\.(?=\s|$)/gu, "for example"],
  [/\bcf\.(?=\s|$)/gu, "compare"],
  [/\betc\.(?=\s|$)/gu, "et cetera"],
  [/\bviz\.(?=\s|$)/gu, "namely"],
  [/\bibid\.(?=\s|$)/gu, "in the same place"],
  [/\bapprox\.(?=\s|$)/gu, "approximately"],
  [/\bc\.\s?(?=\d{3,4}\b)/gu, "circa "],
  [/\bvs\.?(?=\s|$)/gu, "versus"],
  [/\bA\.\s?D\.(?=\s|$)/gu, "A D"],
  [/\bB\.\s?C\.(?=\s|$)/gu, "B C"],
];

/**
 * Units expand ONLY after a number, and the ambiguous ones are absent entirely.
 *
 * `in` is a word before it is inches and `m` is a word in half the world's abbreviations; both are
 * omitted rather than guessed at. This table is deliberately short — a unit read as a letter is a
 * small blemish, a common word rewritten as a unit is a sentence that no longer means what it says.
 */
const UNITS: readonly (readonly [RegExp, string, string])[] = [
  [/(?<=\d\s?)km\b/gu, "kilometre", "kilometres"],
  [/(?<=\d\s?)kg\b/gu, "kilogram", "kilograms"],
  [/(?<=\d\s?)cm\b/gu, "centimetre", "centimetres"],
  [/(?<=\d\s?)mm\b/gu, "millimetre", "millimetres"],
  [/(?<=\d\s?)ml\b/gu, "millilitre", "millilitres"],
  [/(?<=\d\s?)mph\b/gu, "mile per hour", "miles per hour"],
  [/(?<=\d\s?)lbs\b/gu, "pounds", "pounds"],
  [/(?<=\d\s?)oz\b/gu, "ounce", "ounces"],
  [/(?<=\d\s?)ft\b/gu, "foot", "feet"],
  [/(?<=\d\s?)°\s?C\b/gu, "degree Celsius", "degrees Celsius"],
  [/(?<=\d\s?)°\s?F\b/gu, "degree Fahrenheit", "degrees Fahrenheit"],
];

/**
 * `$1,200` was measured correct, so the dollar sign is left exactly as it is. Sterling and euro were
 * NOT measured, and a currency symbol the model does not know is silently dropped — the amount is
 * read and the currency vanishes, which in a book about money is a wrong number with no error.
 */
const CURRENCY: readonly (readonly [RegExp, string])[] = [
  [/£\s?([\d,]+(?:\.\d+)?)/gu, "$1 pounds"],
  [/€\s?([\d,]+(?:\.\d+)?)/gu, "$1 euros"],
  [/¥\s?([\d,]+(?:\.\d+)?)/gu, "$1 yen"],
];

export function expandAbbreviations(text: string): string {
  let out = text;
  for (const [pattern, replacement] of ABBREVIATIONS) out = out.replace(pattern, replacement);
  for (const [pattern, singular, plural] of UNITS) {
    out = out.replace(pattern, (_whole, ...rest) => {
      const offset = rest[rest.length - 2] as number;
      const source = rest[rest.length - 1] as string;
      // "1 km" is singular, everything else is plural. Reading "1 kilometres" is a small, permanent,
      // audible wrongness in exactly the sentences that mention a single unit.
      const before = source.slice(0, offset);
      return /(?:^|\D)1\s?$/u.test(before) ? singular : plural;
    });
  }
  for (const [pattern, replacement] of CURRENCY) out = out.replace(pattern, replacement);
  return out;
}

// ─── 7. initials ─────────────────────────────────────────────────────────────

/**
 * `J. R. R. Tolkien` came back as "JR Token": the stops glue the initials into a token the model
 * tries to pronounce. Spacing them out — `J R R Tolkien` — makes each one a letter again.
 *
 * §4.7 calls this the weakest rule in the file and it is right: a person whose name genuinely reads
 * as an acronym still comes out wrong, and the escape hatch is a per-sentence respelling, which is
 * why `overrides` runs first. Two or more initials are required, so a single `E. Forster` (where the
 * stop is doing sentence work) is untouched.
 */
const INITIAL_RUN = /\b(?:\p{Lu}\.\s*){2,}(?=\p{Lu}\p{Ll})/gu;

export const spaceInitials = (text: string): string =>
  text.replace(INITIAL_RUN, (run) => run.replace(/\./gu, "").replace(/\s+/gu, " ").trim() + " ");

// ─── 8. typography ───────────────────────────────────────────────────────────

/**
 * The cosmetic pass, and the one that is safe everywhere.
 *
 * NFKC is used HERE and nowhere near display text: it folds ligatures and full-width forms, which is
 * destructive for a string that offsets index into (see `shared/text/normalize.ts`) and exactly right
 * for a string that is about to be spoken and then thrown away.
 *
 * An EN DASH BETWEEN DIGITS IS A RANGE, not a pause: `1914–1918` is "1914 to 1918", and turning it
 * into "1914, 1918" would state two dates where the author wrote one span.
 */
export function normaliseTypography(text: string): string {
  // Soft hyphen, zero-width space, word joiner, BOM, and the non-whitespace controls. An EPUB is
  // full of soft hyphens; inside a word they break the TTS text and the clip key while rendering as
  // nothing at all.
  const rewritten = stripInvisible(text.normalize("NFKC"))
      .replace(/(\d)\s*–\s*(\d)/gu, "$1 to $2")
      // Both spellings of the dash, spaced or not. An em dash is read either as nothing or as a
      // word; a comma is what gets the breath, and the visual pause survives in print regardless.
      .replace(/\s*[—–]\s*/gu, ", ")
      // Double quotes are silent on the page and unpredictable in the model. Apostrophes stay:
      // `baker's` and `Nila's` are apostrophes, and stripping those mangles the word.
      .replace(/[“”"«»„‟]/gu, "")
      .replace(/[‘’‚‛]/gu, "'")
      .replace(/\s*(?:…|\.\.\.)\s*/gu, ", ")
      .replace(/\s*&\s*/gu, " and ")
      // ", ." and ", ," come out of the dash and ellipsis rules meeting existing punctuation.
      .replace(/,\s*(?=[,.;:!?])/gu, "")
      .replace(/\s+([,.;:!?])/gu, "$1");

  // The whitespace collapse comes from the one place that owns the rule, not from a fourth spelling
  // of `\s+` — §2.4's duplicated-constant failure applies to regexes as much as to numbers.
  return collapseWhitespace(rewritten);
}

// ─── 9. ALL-CAPS ─────────────────────────────────────────────────────────────

/**
 * A shouted run is a typographic convention (a chapter opening that came through as capitals), not an
 * instruction to spell. Words of FOUR OR MORE capitals are title-cased; two and three are left alone,
 * because `FBI`, `USA` and `BBC` are correctly spelled out as letters and that is what we want.
 *
 * ⚠️ **The unit is the RUN, not the word**, which is §4.7's own wording and it matters. Alice (#11)
 * ends chapter sixteen on the heading `THE END`: word by word that is two three-letter words, both
 * "left alone", and the rule would have done nothing on the one real case in the sample library. So
 * adjacent all-caps words are one run, measured in letters — `THE END` is six and is title-cased,
 * `FBI` is three and is not.
 *
 * The cost is named rather than hidden: a genuine pair of adjacent acronyms (`US GDP`) is title-cased
 * too, and comes out as words. That is a small blemish against a heading in every Gutenberg EPUB
 * being spelled out letter by letter, and the escape hatch is a lexicon entry, which beats this stage
 * outright because overrides run first.
 *
 * ⚠️ **A possessive is part of the word.** `ALICE'S` used to match as `ALICE` + a stranded `S`: the
 * run ended at the apostrophe and a one-letter `S` cannot start a new run, so the result was
 * `Alice'S`. Measured on the real Gutenberg Alice, where it appears in the first heading of the book
 * and in the file's own title. The apostrophe tail is therefore inside the run.
 *
 * `toLowerCase()` and never `toLocaleLowerCase()`: under a Turkish locale the latter maps `I` to a
 * dotless `ı`, which would make `ttsText` — and therefore `clipKey` — depend on the environment of
 * whichever box ran the job. Two boxes would then generate two clips of one sentence.
 */
const CAPS_WORD = String.raw`\p{Lu}{2,}(?:['’]\p{Lu}+)?`;
const CAPS_RUN = new RegExp(`${CAPS_WORD}(?:[ ]${CAPS_WORD})*`, "gu");
/** Letters only — an apostrophe must not count toward the four that decide `FBI` from `THE END`. */
const CAPS_LETTERS = /\p{Lu}/gu;

export const softenAllCaps = (text: string): string =>
  text.replace(CAPS_RUN, (run) => {
    if ((run.match(CAPS_LETTERS) ?? []).length <= 3) return run;
    return run
      .split(" ")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ");
  });

// ─── 10. URLs and emails ─────────────────────────────────────────────────────

/**
 * Dropped, not read. A URL read aloud is thirty seconds of "h t t p colon slash slash" and it is
 * never what the listener wanted.
 *
 * ⚠️ This runs AFTER the typography stage, which has already rewritten `&` into `and`. The patterns
 * below therefore key off the scheme, the `www.` prefix and the `@` — none of which stage 8 touches.
 * A pattern that depended on `&` or on quotes would silently stop matching.
 */
const URL_LIKE = /\b(?:https?:\/\/|www\.)[^\s,;]+/giu;
const EMAIL_LIKE = /\b[^\s@]+@[^\s@]+\.[A-Za-z]{2,}\b/gu;

export const dropUrlsAndEmails = (text: string): string =>
  text
    .replace(URL_LIKE, "")
    .replace(EMAIL_LIKE, "")
    .replace(/\s+/gu, " ")
    .replace(/\s+([.,;:!?])/gu, "$1")
    .trim();

// ─── 11. Devanagari → phonetic Latin ─────────────────────────────────────────

/**
 * The transliterator itself is `shared/text/devanagari.ts`; this stage is the one line that calls it,
 * and everything worth knowing is in that file's header. What belongs HERE is why this is the seam:
 *
 * `ttsText` is the string hashed into `clipKey` and into `sentences.ttsTextSha`, so transliterating
 * at this stage means the reader keeps rendering Devanagari and only the GPU ever sees Latin. Improve
 * a rule later and exactly the sentences whose `ttsText` changed become `missing`. **Do not add a
 * transliteration-version constant to any key** — see this file's header on LAW 7.
 *
 * The gate is the presence of Devanagari in the string, not `blocks.script`, and the function returns
 * its input by identity when there is none. That is a strictly finer test than the per-block one: a
 * Latin block is unchanged either way, and a Devanagari word quoted inside an English sentence — a
 * block `detectScript()` calls `Latn` — is still unspeakable and still gets fixed.
 */

// ─── 12. terminal punctuation ────────────────────────────────────────────────

/**
 * Both TTS briefs flag short inputs as the untested risk — `over`, `Twice.`, `one` — and both name
 * the same cheap mitigation: a full stop usually fixes the rising terminal contour, and it costs
 * nothing. A trailing comma, semicolon or colon already gives the breath and is left alone.
 */
export const ensureTerminal = (text: string): string =>
  text === "" || /[.!?…,;:]$/u.test(text) ? text : `${text}.`;

// ─── 13. splitForSynthesis ───────────────────────────────────────────────────

/**
 * Cut one already-segmented sentence into sub-utterances. NOT a segmenter (LAW 4) — see the header.
 *
 * ⚠️ **We never let Chatterbox chunk for us.** Its server-side `_chunk_text` fires at 450 characters
 * and inserts a pause we cannot control, cannot measure per boundary, and cannot see. Measured
 * 2026-08-07: the model inserts 268–1872 ms between sentences at every request length, so the only
 * way to own the pacing is to own the boundaries.
 *
 * Breaks are chosen at real clause punctuation nearest the middle, so a split sentence is split
 * where a reader would breathe. A break that would leave a fragment under `min` is rejected in favour
 * of a worse-placed one; if nothing qualifies the text is cut at the last space before `max`, and if
 * there is no space (one enormous token, a chemical name, a URL that survived stage 10) it is cut
 * hard rather than posted whole.
 */
const BREAK_PRIORITY: readonly RegExp[] = [/;\s/gu, /:\s/gu, /,\s/gu, /\s/gu];

export function splitForSynthesis(
  text: string,
  options: { max?: number; min?: number } = {},
): string[] {
  const max = options.max ?? SYNTHESIS_MAX_CHARS;
  const min = options.min ?? SYNTHESIS_MIN_CHARS;
  const parts: string[] = [];
  cut(text.trim(), max, min, parts);
  return parts.filter((p) => p !== "");
}

function cut(text: string, max: number, min: number, out: string[]): void {
  if (text.length <= max) {
    out.push(text);
    return;
  }

  const middle = text.length / 2;
  let best = -1;

  for (const pattern of BREAK_PRIORITY) {
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    let bestForPattern = -1;
    while ((match = pattern.exec(text)) !== null) {
      // Break AFTER the punctuation and before the space, so the clause keeps its own comma.
      const at = match.index + match[0].length - 1;
      const left = at;
      const right = text.length - at;
      if (left < min || right < min) continue;
      if (left > max) break; // every later candidate is further right and also too long
      if (bestForPattern < 0 || Math.abs(at - middle) < Math.abs(bestForPattern - middle)) {
        bestForPattern = at;
      }
    }
    if (bestForPattern > 0) {
      best = bestForPattern;
      break;
    }
  }

  if (best < 0) {
    // No qualifying clause break. Cut at the last space that fits; failing that, cut hard — a
    // 450-character token posted whole would be chunked by the server, which is the one outcome
    // this function exists to prevent.
    const space = text.lastIndexOf(" ", max);
    best = space > 0 ? space : max;
  }

  out.push(text.slice(0, best).trim());
  cut(text.slice(best).trim(), max, min, out);
}

// ─── numbers as words ────────────────────────────────────────────────────────

const CARDINAL_ONES: readonly string[] = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten",
  "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen",
];
const CARDINAL_TENS: readonly string[] = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
];
const ORDINAL_ONES: readonly string[] = [
  "zeroth", "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth",
  "eleventh", "twelfth", "thirteenth", "fourteenth", "fifteenth", "sixteenth", "seventeenth", "eighteenth", "nineteenth",
];
const ORDINAL_TENS: readonly string[] = [
  "", "", "twentieth", "thirtieth", "fortieth", "fiftieth", "sixtieth", "seventieth", "eightieth", "ninetieth",
];

/**
 * `119` → `"one hundred and nineteen"`.
 *
 * The `and` is deliberate: this function's busiest caller by far is scripture, where "Psalm one
 * hundred and nineteen" is how the verse is read aloud. Out of range it returns the digits unchanged
 * rather than throwing — a number too large to spell is one the model already handles (years and
 * thousands separators were measured correct), and a normaliser that throws takes a book down.
 */
export function cardinalWords(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 999_999) return String(n);
  if (n < 20) return CARDINAL_ONES[n]!;
  if (n < 100) {
    const tens = CARDINAL_TENS[Math.floor(n / 10)]!;
    const ones = n % 10;
    return ones === 0 ? tens : `${tens}-${CARDINAL_ONES[ones]}`;
  }
  if (n < 1000) {
    const head = `${CARDINAL_ONES[Math.floor(n / 100)]} hundred`;
    const rest = n % 100;
    return rest === 0 ? head : `${head} and ${cardinalWords(rest)}`;
  }
  const head = `${cardinalWords(Math.floor(n / 1000))} thousand`;
  const rest = n % 1000;
  if (rest === 0) return head;
  return rest < 100 ? `${head} and ${cardinalWords(rest)}` : `${head} ${cardinalWords(rest)}`;
}

/** `3` → `"third"`, `21` → `"twenty-first"`. Out of range returns the digits unchanged, as above. */
export function ordinalWords(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 999_999) return String(n);
  if (n < 20) return ORDINAL_ONES[n]!;
  if (n < 100) {
    const ones = n % 10;
    return ones === 0
      ? ORDINAL_TENS[Math.floor(n / 10)]!
      : `${CARDINAL_TENS[Math.floor(n / 10)]}-${ORDINAL_ONES[ones]}`;
  }
  const rest = n % 100;
  const head = cardinalWords(n - rest);
  return rest === 0 ? `${head}th` : `${head} and ${ordinalWords(rest)}`;
}
